/**
 * shared/narration/cue.ts — LAW 5 and LAW 9.
 *
 * LAW 5. THERE IS ONE NOUN FOR A UNIT OF NARRATED TEXT: `page`. Not "segment", not "passage", not
 * "chunk", not "unit", not "piece". And there is ONE cue type, defined here and imported by both
 * sides, so a rename is a type error rather than an `undefined` at runtime.
 *
 * A cue boundary is a FILE boundary: clips are generated one per sentence and stitched into one page
 * artifact, so cue boundaries are exact by construction. There is no alignment step and nothing to
 * desync — which is the whole reason per-sentence generation was chosen, and it is why nothing in
 * this file interpolates.
 */

import type { BookPos } from "../position.ts";

/**
 * One sentence's span in a page artifact, in milliseconds from the start of the file.
 *
 * `w` is reserved for word boundaries and is EMPTY in v1. Word-level highlighting is out, deliberately
 * (spark-1's ASR emits no timestamps — checked against the real API; it also returned "walks" for
 * "walked"). Word-level needs a forced aligner. LINEAR INTERPOLATION ACROSS A SENTENCE IS REJECTED
 * OUTRIGHT: our pacing inserts 60-1200 ms of silence at seams, so a linear sweep desynchronises on
 * every sentence containing a pause — worst on exactly the books (scripture, poetry) this product was
 * asked for. `w` is also where §3.2's sub-clip boundaries from `splitForSynthesis` are emitted, so
 * the reader can use them for turn and scroll decisions WITHOUT changing what a sentence is.
 */
export interface Cue {
  sid: string;
  start: number;
  end: number;
  w?: [number, number][];
}

/** What the planner knows about a page's narration. */
export type PageState =
  | "missing" // no clips, nothing queued
  | "queued" // a job exists, the GPU has not started
  | "building" // generating or stitching right now
  | "ready" // an artifact exists and is current
  | "failed"; // terminal; the reader offers Retry on the sentence menu

/**
 * ⚠️ The UI reports `ready` / `building` and NEVER "cached", to anyone who is not the owner.
 * Clips are content-addressed with no book id in the key, so cross-book dedup is free — and the
 * residual, accepted risk is a cross-book EXISTENCE ORACLE: "cached" tells an uploader that somebody
 * else already narrated that exact normalised text. Cache-hit counts live only in /admin.
 */

export interface PageAudio {
  pageId: string;
  /** LAW 1. A cue's sid is meaningless without this. */
  editionId: string;
  artifactSha: string;
  /** /api/v1/audio/page/:pageId.m4a?v=<artifactSha> — addressed by domain object, never by blob sha (LAW 18). */
  url: string;
  mime: string; // 'audio/mp4; codecs="mp4a.40.2"'
  /** Measured on the trimmed PCM master, never from ffprobe on the container and never from the TTS header. */
  durationMs: number;
  /**
   * REQUIRED. Measured at stitch. Verified 0 for AAC/MP4 — and it is emitted ANYWAY.
   *
   * This one integer is why LAW 9 exists. Narration's own verification concluded no correction was
   * needed and therefore emitted nothing; the reader subtracts it in the rAF loop and adds it in the
   * desync guard, so an absent field would make `masterMs` NaN, make `lowerBound` return a constant
   * index, freeze the highlight for a whole page — and, worse, make `NaN > tolerance` FALSE, so the
   * desync guard would silently disable itself. The one mechanism designed to catch this class of bug
   * is the mechanism the bug disables. Cost of the fix: one integer.
   */
  leadInMs: number;
  cues: Cue[];
  firstSid: string;
  lastSid: string;
}

/**
 * The one spelling of a page artifact's URL, built by the stitch and read by the reader.
 *
 * Addressed by DOMAIN OBJECT — the page — never by blob sha. LAW 18: there is no `GET /blob/:sha`
 * and blob shas are never addressable from outside, because a clip is content-addressed with no book
 * id in the key, so a private book's audio and a public book's audio of the same sentence are
 * literally the same file. A leaked sha must not be a capability.
 *
 * `?v=<artifactSha>` is a content hash, which is what makes the delivery cache immutable and
 * staleness structurally impossible: a regenerated page is a different URL, not a stale entry.
 */
export const pageAudioUrl = (pageId: string, artifactSha: string, ext = "m4a"): string =>
  `/api/v1/audio/page/${encodeURIComponent(pageId)}.${ext}?v=${artifactSha}`;

/**
 * Does this cue list describe this page? Returns a reason when it does not, `null` when it does.
 *
 * The rule is **ordered subset**: every cue must name a sentence the page contains, in the page's own
 * order, once. A page legitimately has sentences with no cue — an image caption, a table, anything
 * `speak: false`, or a sentence whose pronunciation override maps it to "printed but not spoken". A
 * cue naming a sentence the page does NOT contain is the other direction and it is never legitimate:
 * the highlight resolves to nothing, or — if anyone ever "helpfully" snaps it to the nearest span —
 * to the wrong line, while the right audio plays. That is the failure the whole addressing spine
 * exists to prevent, and the listener blames themselves for having misread.
 *
 * One predicate, called by the stitch before an artifact is written and by the reader's desync guard
 * before a frame is painted. Two implementations of one rule diverge on the case nobody screenshotted.
 */
export function cueSidsMatch(cues: readonly Cue[], sids: readonly string[]): string | null {
  const order = new Map<string, number>();
  sids.forEach((sid, i) => {
    if (!order.has(sid)) order.set(sid, i);
  });

  let last = -1;
  const seen = new Set<string>();
  for (const cue of cues) {
    const at = order.get(cue.sid);
    if (at === undefined) return `cue names ${cue.sid}, which is not on this page`;
    if (seen.has(cue.sid)) return `${cue.sid} has two cues on one page`;
    if (at < last) return `${cue.sid} is out of the page's reading order`;
    seen.add(cue.sid);
    last = at;
  }
  return null;
}

// ─── the reader's constants. One home each. ──────────────────────────────────

/**
 * The wash must LEAD the voice slightly; lagging reads as "the app is broken", not "the timer is
 * coarse". P2's gate includes judging this BY EAR on Karti's phone — if it reads as leading or
 * lagging, this constant is the fix, and it is the only place to change it.
 */
export const CUE_LEAD_MS = 45;

/**
 * A page turn is SCHEDULED against the audio clock, never fired on a `timeupdate` (Safari fires that
 * at ~4 Hz with jitter, so the turn lands up to 250 ms late and reads as a stutter). The lead makes
 * the 220 ms slide two-thirds done when the first word of the new page lands: the eye arrives before
 * the voice.
 */
export const TURN_LEAD_MS = 140;

/**
 * 150 ms, not 60. An AAC frame is 1024 samples = 42.67 ms at 24 kHz, so container duration is
 * quantised by more than 60 ms even in the good case — a 60 ms tolerance fires the guard on healthy
 * pages, and a guard that cries wolf gets deleted.
 *
 * A duration mismatch ALONE never disables the read-along. The guard additionally requires that the
 * first cue's sid fails to resolve in the DOM.
 */
export const DESYNC_TOLERANCE_MS = 150;

// ─── cue math. LAW 3: the client resolves offsets from ITS OWN cue list. ─────

export const findCue = (cues: readonly Cue[], sid: string): Cue | undefined =>
  cues.find((c) => c.sid === sid);

/**
 * LAW 3, as one function. `ms` on the wire is advisory; THIS is how a position becomes a time.
 *
 *   t = cue.start + frac * (cue.end - cue.start)
 *
 * Trusting a server-sent millisecond figure against a slightly different artifact is how a client
 * highlights confidently and wrongly, tens of seconds off by mid-book.
 */
export const resolveCueMs = (cue: Cue, frac: number): number =>
  cue.start + frac * (cue.end - cue.start);

/** The inverse, for reporting a position. Clamped to 0..1 — a frac outside it is not a position. */
export function fracInCue(cue: Cue, ms: number): number {
  const span = cue.end - cue.start;
  if (span <= 0) return 0;
  const f = (ms - cue.start) / span;
  return f < 0 ? 0 : f > 1 ? 1 : f;
}

/** Resolve a `BookPos` against a page's own cues. Null when the sid is not on this page — never a guess. */
export function msOfPos(page: PageAudio, at: BookPos): number | null {
  const cue = findCue(page.cues, at.sid);
  return cue ? resolveCueMs(cue, at.frac) : null;
}

/**
 * Index of the cue containing `ms`, or -1 when `ms` is before the first cue.
 *
 * ⚠️ THE -1 IS NOT DEFENSIVE PADDING AND CALLERS MUST HANDLE IT. §4.2 puts 620-900 ms of `pageHead`
 * silence at the head of every page, so `cues[0].start >= 620` and this returns -1 for the first
 * two-thirds of a second of EVERY page. Dereferencing `cues[-1].sid` throws inside a rAF callback,
 * the loop is never rescheduled, and the page plays with no highlight and no turn — recovering at the
 * next page, so it reads to the user as "flaky" rather than as a crash.
 *
 * Binary search: this runs every animation frame.
 */
export function cueIndexAt(cues: readonly Cue[], ms: number): number {
  let lo = 0;
  let hi = cues.length - 1;
  let ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (cues[mid]!.start <= ms) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}

// ─── LAW 9, made callable ────────────────────────────────────────────────────

/**
 * LAW 9. ABSENT ≠ ZERO ON THE WIRE. A missing or non-finite numeric field is a HARD failure, never
 * coerced. `start ?? 0` is how a dropped field becomes a page that highlights its first sentence
 * forever, and `NaN` is how a desync guard silently switches itself off.
 */
export const isFiniteMs = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

export type PageAudioCheck =
  | { ok: true; page: PageAudio }
  | { ok: false; reason: string };

/**
 * Validate a `PageAudio` off the wire before a single frame is rendered against it.
 *
 * On failure the caller drops the READ-ALONG with a visible reason and PLAYS THE AUDIO ANYWAY. Fail
 * safe, loudly, in that order: a silent desync is worse than a missing feature, because the user
 * blames themselves for having misread.
 */
export function validatePageAudio(p: unknown): PageAudioCheck {
  if (typeof p !== "object" || p === null) return { ok: false, reason: "page-audio-not-an-object" };
  const q = p as Partial<PageAudio>;

  if (typeof q.pageId !== "string" || q.pageId === "") return { ok: false, reason: "missing-pageId" };
  if (typeof q.editionId !== "string" || q.editionId === "") return { ok: false, reason: "missing-editionId" };
  if (typeof q.artifactSha !== "string" || q.artifactSha === "") return { ok: false, reason: "missing-artifactSha" };
  if (!isFiniteMs(q.durationMs)) return { ok: false, reason: "missing-durationMs" };
  if (!isFiniteMs(q.leadInMs)) return { ok: false, reason: "missing-leadInMs" };
  if (!Array.isArray(q.cues)) return { ok: false, reason: "missing-cues" };

  let prevStart = -1;
  for (const c of q.cues) {
    if (typeof c?.sid !== "string" || c.sid === "") return { ok: false, reason: "cue-missing-sid" };
    if (!isFiniteMs(c.start) || !isFiniteMs(c.end)) return { ok: false, reason: `cue-missing-bound:${c.sid}` };
    if (c.end < c.start) return { ok: false, reason: `cue-inverted:${c.sid}` };
    // Non-decreasing starts are what makes `cueIndexAt`'s binary search correct. An out-of-order cue
    // list does not throw at render time; it silently highlights the wrong sentence.
    if (c.start < prevStart) return { ok: false, reason: `cue-out-of-order:${c.sid}` };
    prevStart = c.start;
    // `w` is empty in v1 for word boundaries, but the stitch DOES emit it for the sub-clip boundaries
    // of a sentence that had to be split for synthesis. Same law, one level down: a non-finite bound
    // in here reaches whatever turn or scroll decision reads it, and `NaN > x` is false everywhere.
    if (c.w !== undefined) {
      if (!Array.isArray(c.w)) return { ok: false, reason: `cue-w-not-an-array:${c.sid}` };
      for (const pair of c.w) {
        if (!Array.isArray(pair) || pair.length !== 2 || !isFiniteMs(pair[0]) || !isFiniteMs(pair[1])) {
          return { ok: false, reason: `cue-w-bound:${c.sid}` };
        }
      }
    }
  }
  return { ok: true, page: q as PageAudio };
}
